import { EMULATOR_DEV_MODE, useAuth } from './AuthProvider'

/** Google's mark, inlined — no external asset requests on the sign-in screen. */
function GoogleMark() {
  return (
    <svg className="size-5" viewBox="0 0 48 48" aria-hidden="true">
      <path
        fill="#4285F4"
        d="M45.12 24.5c0-1.56-.14-3.06-.4-4.5H24v8.51h11.84c-.51 2.75-2.06 5.08-4.39 6.64v5.52h7.11c4.16-3.83 6.56-9.47 6.56-16.17z"
      />
      <path
        fill="#34A853"
        d="M24 46c5.94 0 10.92-1.97 14.56-5.33l-7.11-5.52c-1.97 1.32-4.49 2.1-7.45 2.1-5.73 0-10.58-3.87-12.31-9.07H4.34v5.7C7.96 41.07 15.4 46 24 46z"
      />
      <path
        fill="#FBBC05"
        d="M11.69 28.18C11.25 26.86 11 25.45 11 24s.25-2.86.69-4.18v-5.7H4.34C2.85 17.09 2 20.45 2 24s.85 6.91 2.34 9.88l7.35-5.7z"
      />
      <path
        fill="#EA4335"
        d="M24 10.75c3.23 0 6.13 1.11 8.41 3.29l6.31-6.31C34.91 4.18 29.93 2 24 2 15.4 2 7.96 6.93 4.34 14.12l7.35 5.7c1.73-5.2 6.58-9.07 12.31-9.07z"
      />
    </svg>
  )
}

export function SignIn() {
  const { state, signIn, signOut, error } = useAuth()

  // Signed in with a Google account that isn't allowed. Say so plainly and
  // offer the way out, rather than showing an app that fails every query.
  if (state.status === 'not-allowed') {
    return (
      <Centered>
        <h1 className="text-2xl font-semibold tracking-tight">Not authorized</h1>
        <p className="text-slate-600">
          You're signed in as <span className="font-medium">{state.email}</span>, which
          doesn't have access to this system.
        </p>
        <button
          type="button"
          onClick={() => void signOut()}
          className="mt-2 rounded-lg bg-slate-900 px-5 py-3 font-medium text-white transition hover:bg-slate-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
        >
          Sign in with a different account
        </button>
      </Centered>
    )
  }

  return (
    <Centered>
      {/* Decorative: the heading below already names the app, so alt text here
          would just make a screen reader say "BudgetMonkey" twice. Sized in
          rem rather than viewport units so it doesn't balloon on a wide
          monitor. Eager, because it is the only thing above the fold and a
          lazy logo pops in after the page settles. */}
      <img
        src="/logo.png"
        alt=""
        width={144}
        height={144}
        className="size-32 sm:size-36"
      />

      {/* The wordmark, only here. `sup` rather than a bare ™ so it sits raised
          and small; `aria-hidden` because a screen reader announcing "trade
          mark" after the app name is noise, not information. */}
      <h1 className="text-3xl font-semibold tracking-tight">
        Budget Monkey
        <sup aria-hidden="true" className="ml-0.5 align-super text-xs font-normal">
          ™
        </sup>
      </h1>
      <p className="text-slate-600">P-Card and budget tracking</p>

      <button
        type="button"
        onClick={() => void signIn()}
        className="mt-4 inline-flex items-center justify-center gap-3 rounded-lg border border-slate-300 bg-white px-5 py-3 font-medium text-slate-900 shadow-sm transition hover:bg-slate-50 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-slate-900"
      >
        <GoogleMark />
        Sign in with Google
      </button>

      {error !== null && (
        <p role="alert" className="text-sm text-red-700">
          {error}
        </p>
      )}

      <EmulatorSignIn />
    </Centered>
  )
}

/**
 * Local-development shortcut. Renders only when the dev server is pointed at
 * the emulators; the whole component is compiled out of production builds.
 * Both accounts are offered so the allowlist rejection path stays easy to
 * exercise.
 */
function EmulatorSignIn() {
  const { signInAsEmulator } = useAuth()
  if (!EMULATOR_DEV_MODE || signInAsEmulator === undefined) return null

  return (
    <div className="mt-8 w-full space-y-2 rounded-lg border border-dashed border-amber-400 bg-amber-50 p-4">
      <p className="text-xs font-medium text-amber-900">
        Emulator mode — dev only, not in production builds
      </p>
      <div className="flex flex-wrap gap-2">
        <button
          type="button"
          onClick={() => void signInAsEmulator('mdcallison@southfayette.org')}
          className="rounded border border-amber-400 bg-white px-3 py-1.5 text-sm"
        >
          Sign in as Matt (allowed)
        </button>
        <button
          type="button"
          onClick={() => void signInAsEmulator('stranger@gmail.com')}
          className="rounded border border-amber-400 bg-white px-3 py-1.5 text-sm"
        >
          Sign in as stranger (blocked)
        </button>
      </div>
    </div>
  )
}

function Centered({ children }: { children: React.ReactNode }) {
  // `items-start` left-aligned everything inside a column that was itself
  // centred, so the whole sign-in block sat off to one side of the page and
  // read as a layout bug. Centred on both axes now, which is what a page with
  // one button on it should look like.
  return (
    <main className="mx-auto flex min-h-dvh max-w-md flex-col items-center justify-center gap-3 px-6 py-16 text-center">
      {children}
    </main>
  )
}
